/**
 * Echo Hunt -- live (DB-backed) wiring for the fake generator (P6).
 *
 * Per the ground rules (§16.0), the generation logic lives in the pure `FakeGenerator.ts`; this thin
 * `*_Live.ts` wrapper is where the corpus rows and the user's profile settings are read.
 *
 * Two things are assembled here:
 *  1. the DISTRIBUTIONS fakes must match -- `eventType` (§5.3), `oddityCount` (§5.2 concept count) and
 *     the word-count band -- measured over the USABLE cached corpus bullets. Measuring them on the
 *     same rows the round will sample reals from is what keeps the two classes comparable;
 *  2. the filtered SEED-WORD list, with the user's cutoffs, excluded tags and banned terms applied.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §5, §16.2 P6.
 */
import {EchoCorpusBullet} from "../Server/DB/echoCorpusBullets/@EchoCorpusBullet.js";
import {GetMyEchoCorpusBullets} from "./Index_Live.js";
import {GetMyEchoProfile} from "./Profiles_Live.js";
import {EchoWordlistSettings} from "./Types.js";
import {UpdateEchoProfile} from "../Server/Commands/EchoProfileCommands.js";
import {DefaultWordlistSettings, WordlistAvailableForGeneration} from "./Wordlist.js";
import {
	BuildFakeDistributions,
	FakeGenerationDistributions,
	GenerateFake,
	GenerateFakeResult,
	GenerateFakes,
	GenerateFakesOptions,
} from "./FakeGenerator.js";
import {liveLLMBackend} from "./LLM_Live.js";
import {LLMCallCounter} from "./LLM.js";
import {GetSeedWordlist} from "./Wordlist_Live.js";

/** The usable corpus rows (unfiltered bullets only) -- the pool §5.3's distributions are measured on
 * and the pool a round samples reals from. */
export function GetUsableCorpusRows(): EchoCorpusBullet[] {
	return GetMyEchoCorpusBullets().filter(row=>row.filterReason == null && (row.selfContainedText ?? "").trim().length > 0);
}

/** The distributions a fake must match, measured on the usable corpus (§5.2, §5.3). Returns empty
 * distributions (not an error) when nothing is indexed yet, since the P6 dev tool is usable before a
 * full index run and the samplers all have explicit fallbacks. */
export function GetFakeDistributions(): FakeGenerationDistributions {
	return BuildFakeDistributions(GetUsableCorpusRows().map(row=>({
		eventType: row.eventType,
		oddityCount: row.oddityCount,
		text: row.selfContainedText ?? "",
	})));
}

/** The user's word-list settings, defaulted for a profile that predates the field. */
export function GetWordlistSettings(): EchoWordlistSettings {
	return GetMyEchoProfile()?.wordlist ?? DefaultWordlistSettings();
}

/** True when the word list has finished loading and yields at least one seed word. The dev tool and
 * the future round builder both check this before starting a batch. */
export function IsSeedWordlistReady(): boolean {
	const profile = GetMyEchoProfile();
	const result = GetSeedWordlist(profile);
	return result != null && WordlistAvailableForGeneration(result);
}

/** Writes the user's word-list cutoffs/tags to their profile (the Settings tab). */
export async function SetWordlistSettings_Live(profileID: string, settings: EchoWordlistSettings): Promise<void> {
	await new UpdateEchoProfile({id: profileID, updates: {wordlist: settings}}).RunOnServer();
}

/** The user's seed-concept-count range, defaulted for a profile that predates the field. */
export function GetConceptCountRange(): {min: number, max: number} {
	const settings = GetWordlistSettings();
	return {min: settings.seedConceptCountMin, max: settings.seedConceptCountMax};
}

/** Generates ONE fake using the live corpus + profile + network backend (the dev tool's per-call
 * path). */
export async function GenerateFake_Live(options: {rng: () => number, blacklist?: string[]|n, counter?: LLMCallCounter|n, model?: string|n, maxAttempts?: number|n}): Promise<GenerateFakeResult|null> {
	const profile = GetMyEchoProfile();
	const wordlist = GetSeedWordlist(profile);
	if (wordlist == null || !WordlistAvailableForGeneration(wordlist)) return null;

	return GenerateFake(liveLLMBackend, {
		wordlist: wordlist.words,
		distributions: GetFakeDistributions(),
		conceptCountRange: GetConceptCountRange(),
		rng: options.rng,
		blacklist: options.blacklist ?? (profile?.clicheBlacklist ?? []).map(a=>a.phrase),
		bannedWords: profile?.bannedTerms ?? [],
		model: options.model,
		counter: options.counter,
		maxAttempts: options.maxAttempts ?? undefined,
	});
}

/** Generates a batch of independent fakes (the Settings tab's "generate 10 fakes" dev tool, §16.2
 * P6). Returns the per-call results so the UI can show seeds -> output -> violations for each. */
export async function GenerateFakes_Live(options: {count: number, rng: () => number, generatorModels?: string[]|n, counter?: LLMCallCounter|n, model?: string|n, maxAttempts?: number|n}): Promise<GenerateFakeResult[]> {
	const profile = GetMyEchoProfile();
	const wordlist = GetSeedWordlist(profile);
	if (wordlist == null || !WordlistAvailableForGeneration(wordlist)) return [];

	const batchOptions: GenerateFakesOptions = {
		wordlist: wordlist.words,
		distributions: GetFakeDistributions(),
		conceptCountRange: GetConceptCountRange(),
		blacklist: (profile?.clicheBlacklist ?? []).map(a=>a.phrase),
		bannedWords: profile?.bannedTerms ?? [],
		rng: options.rng,
		count: options.count,
		generatorModels: options.generatorModels,
		model: options.model,
		counter: options.counter,
		maxAttempts: options.maxAttempts ?? undefined,
	};
	return GenerateFakes(liveLLMBackend, batchOptions);
}
